// Nuffies: silent product clips (snippets/nuffies-autoplay-video.liquid) play only while at least half of
// the clip is on screen: scrolled into view, or swiped to in a product card / product page gallery.
// Off screen (swiped past, scrolled away, tab hidden, closed dialog) they pause, and they start again from
// the beginning the next time they come into view, so shoppers always see the towel on the grass first.
//
// Instant start: as soon as a gallery, card or clip comes near the screen, the whole clip (one small MP4)
// is downloaded once in the background and kept in memory. Every copy of the clip on the page (all the
// towel cards, the looping copies, the homepage tile, the "You may also like" strips) then plays from that
// copy, so swiping to it starts straight away, every time, instead of pausing to load. (Phones ignore the
// usual "preload" hint, which is why the clip used to wait about a second whenever it was swiped to.) If
// that download fails, the clip simply streams as before.
//
// No first-frame flicker: a clip stays invisible (the still underneath shows) until the browser has
// actually drawn a frame of it, then it's revealed. Before a stop it's hidden again first, and only then
// rewound, so the rewind is never seen.
//
// Clips added later (looping copies in the card carousels, section re-renders, quick add, theme editor)
// are picked up automatically. "Reduce motion" keeps the still.

const SELECTOR = 'video.nf-autoplay-video';
const VISIBLE = 0.5;
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const watched = new WeakSet();
const onScreen = new Set();

// --- Download once, play from memory ------------------------------------------------------------------
const memory = new Map(); // clip address -> Promise<blob address | null>

const clipAddress = (video) => {
  const source = video.querySelector('source');
  return (source && source.src) || video.getAttribute('src') || '';
};

const download = (address) => {
  if (!memory.has(address)) {
    memory.set(
      address,
      fetch(address, { credentials: 'omit' })
        .then((response) => (response.ok ? response.blob() : null))
        .then((blob) => (blob && blob.size ? URL.createObjectURL(blob) : null))
        .catch(() => null)
    );
  }
  return memory.get(address);
};

const useMemoryCopy = (video) => {
  if (video.dataset.nfLocal) return;
  const address = clipAddress(video);
  if (!address || address.startsWith('blob:')) return;
  video.dataset.nfLocal = 'pending';
  download(address).then((local) => {
    if (!local || !video.isConnected) return;
    // Already playing from the network: leave it be (it'll use the memory copy next time round).
    if (!video.paused && video.currentTime > 0) {
      video.addEventListener('pause', () => swap(video, local), { once: true });
      return;
    }
    swap(video, local);
  });
};

const swap = (video, local) => {
  if (video.getAttribute('src') === local) return;
  const wanted = onScreen.has(video);
  video.classList.remove('is-playing');
  video.preload = 'auto';
  video.src = local; // takes over from the <source> inside
  video.dataset.nfLocal = 'ready';
  video.load();
  if (wanted) play(video);
};

// Start the download when the clip's gallery / card / tile / photo strip is within about a screen of view.
// (The clip itself can't be watched for this: inside a carousel it counts as off screen until it's swiped to.)
const holderOf = (video) =>
  video.closest('slideshow-component, media-gallery, .card-gallery, .nf-magnet-feature, product-card, .nf-swipe') ||
  video.parentElement ||
  video;

const nearby = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      nearby.unobserve(entry.target);
      entry.target.querySelectorAll(SELECTOR).forEach(useMemoryCopy);
    }
  },
  { rootMargin: '100% 0px' }
);

// --- Play while on screen ------------------------------------------------------------------------------
// Reveal once a real frame is on screen (not just when play() was asked for).
const revealWhenDrawn = (video) => {
  if (video.classList.contains('is-playing')) return;
  const show = () => {
    if (!video.paused) video.classList.add('is-playing');
  };
  if (typeof video.requestVideoFrameCallback === 'function') {
    video.requestVideoFrameCallback(() => requestAnimationFrame(show));
    return;
  }
  const onTime = () => {
    if (video.currentTime <= 0) return;
    video.removeEventListener('timeupdate', onTime);
    show();
  };
  video.addEventListener('timeupdate', onTime);
};

const play = (video) => {
  if (reduceMotion.matches || document.visibilityState !== 'visible') return;
  video.muted = true; // muted is what lets browsers autoplay without a tap
  if (!video.paused) return;
  const attempt = video.play();
  if (attempt) {
    attempt.then(() => revealWhenDrawn(video)).catch(() => {}); // Low Power Mode etc.: the still stays
  } else {
    revealWhenDrawn(video);
  }
};

const stop = (video) => {
  video.classList.remove('is-playing');
  if (!video.paused) video.pause();
  if (video.currentTime > 0) {
    // Rewind on the next frame, once the clip is hidden again.
    requestAnimationFrame(() => {
      if (video.paused) video.currentTime = 0;
    });
  }
};

const observer = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      const video = entry.target;
      if (entry.isIntersecting && entry.intersectionRatio >= VISIBLE) {
        onScreen.add(video);
        useMemoryCopy(video);
        play(video);
      } else {
        onScreen.delete(video);
        stop(video);
      }
    }
  },
  { threshold: [0, VISIBLE, 1] }
);

const watch = (root) => {
  const videos = root.matches?.(SELECTOR) ? [root] : root.querySelectorAll?.(SELECTOR) ?? [];
  for (const video of videos) {
    if (watched.has(video)) continue;
    watched.add(video);
    video.muted = true;
    video.classList.remove('is-playing'); // a looping copy can be cloned mid-play
    // A copy cloned from a clip that already uses the memory copy carries its src; re-check it either way.
    if (video.dataset.nfLocal !== 'ready' || !video.getAttribute('src')) delete video.dataset.nfLocal;
    observer.observe(video);
    if (memory.has(clipAddress(video))) useMemoryCopy(video);
    else nearby.observe(holderOf(video));
  }
};

watch(document);

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node.nodeType === 1) watch(node);
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });

document.addEventListener('visibilitychange', () => {
  for (const video of onScreen) {
    if (document.visibilityState === 'visible') play(video);
    else video.pause();
  }
});

reduceMotion.addEventListener?.('change', () => {
  for (const video of onScreen) {
    if (reduceMotion.matches) stop(video);
    else play(video);
  }
});
